/**
 * Budget editor (spec §13): three levels of detail — simple, staircase,
 * detailed categories — plus Must-Spend/Like-to-Spend tagging and the
 * one-click go-go / slow-go / no-go age-band template.
 */
import { useMemo } from 'react';
import type { BudgetCategory, ExpenseFrequency, Plan } from '../model/types';
import type { YearSnapshot } from '../engine/snapshot';
import { runProjection } from '../engine/projection';
import { CurrencyField, NumberField } from './Inputs';
import { fmtCompactUsd, fmtUsd } from './format';

interface Props {
  plan: Plan;
  update: (mut: (p: Plan) => void) => void;
}

const FREQ_LABELS: Record<ExpenseFrequency, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
  one_time: 'One-time (start year)',
};

const STARTER_CATEGORIES: Omit<BudgetCategory, 'id'>[] = [
  { label: 'Housing (rent/utilities/maintenance)', amount: 2_000, frequency: 'monthly', tier: 'essential' },
  { label: 'Groceries', amount: 800, frequency: 'monthly', tier: 'essential' },
  { label: 'Transportation', amount: 500, frequency: 'monthly', tier: 'essential' },
  { label: 'Healthcare out-of-pocket', amount: 4_000, frequency: 'annual', tier: 'essential', inflationRateOverride: 0.055 },
  { label: 'Insurance', amount: 1_200, frequency: 'quarterly', tier: 'essential' },
  { label: 'Travel', amount: 8_000, frequency: 'annual', tier: 'discretionary' },
  { label: 'Dining out & entertainment', amount: 500, frequency: 'monthly', tier: 'discretionary' },
  { label: 'Gifts & charity', amount: 3_000, frequency: 'annual', tier: 'discretionary', deductible: true },
];

function OutflowBreakdown({ plan }: { plan: Plan }) {
  const rows = useMemo(() => {
    const r = runProjection(plan, 2026);
    const firstRetired = r.snapshots.find(
      (s) => s.wagesIncome + s.selfEmploymentIncome === 0,
    );
    const build = (s: YearSnapshot, title: string) => {
      const f = s.cumulativeInflationFactor; // show in today's dollars
      const items: [string, number][] = [
        ['Budget spending', s.totalSpending / f],
        ['Healthcare & long-term care', (s.healthcarePremiums + s.healthcareOOP + s.ltcNetCost) / f],
        ['Debt payments', s.debtService / f],
        ['Property costs', (s.propertyCosts + s.propertyPurchaseOutflow) / f],
        ["Taxes (last year's bill)", s.priorYearTaxPaid / f],
        ['One-time expenses', s.oneTimeOutflows / f],
      ];
      return { title, items: items.filter(([, v]) => v > 0.5), total: s.totalOutflow / f };
    };
    const out = [build(r.snapshots[0]!, `This year (${r.snapshots[0]!.year})`)];
    if (firstRetired) {
      out.push(build(firstRetired, `First retirement year (${firstRetired.year})`));
    }
    return out;
  }, [plan]);

  return (
    <div className="item-card" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
      <strong style={{ fontSize: 'var(--fs-small)' }}>What a year really costs</strong>
      <p className="hint" style={{ margin: '4px 0 10px' }}>
        The budget below is only your living expenses. Debts, healthcare, property, and taxes
        are added on top by the projection — don't include loan payments here, or they'd be
        counted twice. Today's dollars.
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
        {rows.map((col) => (
          <table key={col.title} className="report-table" style={{ maxWidth: 340 }}>
            <thead>
              <tr><th colSpan={2}>{col.title}</th></tr>
            </thead>
            <tbody>
              {col.items.map(([label, v]) => (
                <tr key={label}>
                  <td>{label}</td>
                  <td style={{ textAlign: 'right' }}>{fmtUsd(Math.round(v))}</td>
                </tr>
              ))}
              <tr>
                <td><strong>Total outflow</strong></td>
                <td style={{ textAlign: 'right' }}><strong>{fmtUsd(Math.round(col.total))}</strong></td>
              </tr>
            </tbody>
          </table>
        ))}
      </div>
    </div>
  );
}

export function BudgetTab({ plan, update }: Props) {
  const b = plan.budget;

  return (
    <div>
      <OutflowBreakdown plan={plan} />
      <div className="seg" role="group" aria-label="Budget detail level" style={{ marginBottom: 'var(--space-4)' }}>
        {(['simple', 'staircase', 'detailed'] as const).map((m) => (
          <button key={m} aria-pressed={b.mode === m}
            onClick={() => update((p) => {
              p.budget.mode = m;
              if (m === 'staircase' && p.budget.baseAnnualAmount == null) {
                p.budget.baseAnnualAmount = p.budget.annualAmount ?? 70_000;
                p.budget.steps ??= [];
              }
              if (m === 'detailed' && !p.budget.categories?.length) {
                p.budget.categories = STARTER_CATEGORIES.map((c, i) => ({ ...c, id: `cat_${i + 1}` }));
              }
            })}>
            {m === 'simple' ? 'Simple' : m === 'staircase' ? 'Staircase' : 'Detailed'}
          </button>
        ))}
      </div>

      {b.mode === 'simple' && (
        <div className="inputs-grid">
          <CurrencyField label="Annual spending" value={b.annualAmount ?? 0}
            hint="Today's dollars, before taxes"
            onCommit={(v) => update((p) => { p.budget.annualAmount = v; })} />
          <NumberField label="Retirement spending %" min={40} max={150}
            value={Math.round((b.retirementSpendingMultiplier ?? 1) * 100)}
            hint="Many retirees spend ~80-90% of working-years budget"
            onCommit={(v) => update((p) => { p.budget.retirementSpendingMultiplier = v / 100; })} />
          <NumberField label="Essential share %" min={0} max={100}
            value={Math.round((b.essentialFraction ?? 0.65) * 100)}
            hint="Must-spend: housing, food, insurance — vs. like-to-spend"
            onCommit={(v) => update((p) => { p.budget.essentialFraction = v / 100; })} />
        </div>
      )}

      {b.mode === 'staircase' && (
        <div>
          <div className="inputs-grid">
            <CurrencyField label="Base annual spending" value={b.baseAnnualAmount ?? 0}
              hint="Today's dollars, until the first step"
              onCommit={(v) => update((p) => { p.budget.baseAnnualAmount = v; })} />
            <NumberField label="Essential share %" min={0} max={100}
              value={Math.round((b.essentialFraction ?? 0.65) * 100)}
              onCommit={(v) => update((p) => { p.budget.essentialFraction = v / 100; })} />
          </div>
          <h3 style={{ margin: 'var(--space-4) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
            Step changes
          </h3>
          {(b.steps ?? []).map((step, i) => (
            <div key={i} className="inputs-grid item-card" style={{ padding: 'var(--space-3) var(--space-4)' }}>
              <div className="field">
                <label>
                  Label
                  <input value={step.label ?? ''} placeholder="Tuition ends, car paid off…"
                    onChange={(e) => update((p) => { p.budget.steps![i]!.label = e.target.value; })} />
                </label>
              </div>
              <NumberField label="From year" value={step.startYear} min={2026} max={2100}
                onCommit={(v) => update((p) => { p.budget.steps![i]!.startYear = v; })} />
              <CurrencyField label="New annual spending" value={step.annualAmount}
                onCommit={(v) => update((p) => { p.budget.steps![i]!.annualAmount = v; })} />
              <div className="field" style={{ justifyContent: 'flex-end' }}>
                <button className="btn danger"
                  onClick={() => update((p) => { p.budget.steps!.splice(i, 1); })}>
                  Remove step
                </button>
              </div>
            </div>
          ))}
          <button className="btn"
            onClick={() => update((p) => {
              p.budget.steps ??= [];
              p.budget.steps.push({
                startYear: 2030,
                annualAmount: p.budget.baseAnnualAmount ?? 70_000,
              });
            })}>
            + Add step
          </button>
        </div>
      )}

      {b.mode === 'detailed' && (
        <div>
          {(b.categories ?? []).map((cat, i) => (
            <details key={cat.id} className="item-card">
              <summary>
                <strong>{cat.label || 'Category'}</strong>
                <span className="sub">
                  {fmtCompactUsd(cat.amount)} {FREQ_LABELS[cat.frequency].toLowerCase()} ·{' '}
                  {cat.tier === 'essential' ? 'must-spend' : 'like-to-spend'}
                  {cat.deductible ? ' · deductible' : ''}
                </span>
              </summary>
              <div className="inputs-grid">
                <div className="field">
                  <label>
                    Label
                    <input value={cat.label}
                      onChange={(e) => update((p) => { p.budget.categories![i]!.label = e.target.value; })} />
                  </label>
                </div>
                <CurrencyField label="Amount" value={cat.amount}
                  onCommit={(v) => update((p) => { p.budget.categories![i]!.amount = v; })} />
                <div className="field">
                  <label>
                    Frequency
                    <select value={cat.frequency}
                      onChange={(e) => update((p) => {
                        p.budget.categories![i]!.frequency = e.target.value as ExpenseFrequency;
                        if (e.target.value === 'one_time') p.budget.categories![i]!.startYear ??= 2030;
                      })}>
                      {(Object.keys(FREQ_LABELS) as ExpenseFrequency[]).map((f) => (
                        <option key={f} value={f}>{FREQ_LABELS[f]}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="field">
                  <label>
                    Kind
                    <select value={cat.tier}
                      onChange={(e) => update((p) => {
                        p.budget.categories![i]!.tier = e.target.value as 'essential' | 'discretionary';
                      })}>
                      <option value="essential">Must-spend (essential)</option>
                      <option value="discretionary">Like-to-spend (discretionary)</option>
                    </select>
                  </label>
                </div>
                {cat.frequency === 'one_time' ? (
                  <NumberField label="Year" value={cat.startYear ?? 2030} min={2026} max={2100}
                    onCommit={(v) => update((p) => { p.budget.categories![i]!.startYear = v; })} />
                ) : (
                  <>
                    <NumberField label="Starts (year, optional)" value={cat.startYear ?? 2026} min={2026} max={2100}
                      onCommit={(v) => update((p) => { p.budget.categories![i]!.startYear = v === 2026 ? undefined : v; })} />
                    <NumberField label="Ends (year, optional)" value={cat.endYear ?? 2100} min={2026} max={2100}
                      onCommit={(v) => update((p) => { p.budget.categories![i]!.endYear = v === 2100 ? undefined : v; })} />
                  </>
                )}
                <NumberField label="Its own inflation % (optional)" min={0} max={12}
                  value={Math.round((cat.inflationRateOverride ?? 0) * 100)}
                  hint="0 = use general inflation"
                  onCommit={(v) => update((p) => {
                    p.budget.categories![i]!.inflationRateOverride = v === 0 ? undefined : v / 100;
                  })} />
                <div className="field">
                  <label>
                    Tax-deductible?
                    <select value={cat.deductible ? 'yes' : 'no'}
                      onChange={(e) => update((p) => {
                        p.budget.categories![i]!.deductible = e.target.value === 'yes' || undefined;
                      })}>
                      <option value="no">No</option>
                      <option value="yes">Yes (charity, mortgage interest…)</option>
                    </select>
                  </label>
                </div>
              </div>
              <button className="btn danger"
                onClick={() => update((p) => { p.budget.categories!.splice(i, 1); })}>
                Remove category
              </button>
            </details>
          ))}
          <button className="btn"
            onClick={() => update((p) => {
              p.budget.categories ??= [];
              p.budget.categories.push({
                id: `cat_${p.budget.categories.length + 1}_${Math.abs(p.budget.categories.length * 7 + 13)}`,
                label: 'New category', amount: 0, frequency: 'monthly', tier: 'discretionary',
              });
            })}>
            + Add category
          </button>
        </div>
      )}

      {/* Age-banded spending — applies to like-to-spend in every mode. */}
      <h3 style={{ margin: 'var(--space-6) 0 var(--space-2)', fontSize: 'var(--fs-body)' }}>
        Spending by life phase
      </h3>
      <p className="hint" style={{ marginTop: 0 }}>
        Real retirees spend less on the fun stuff through their 70s and 80s (the go-go /
        slow-go / no-go pattern). This scales only like-to-spend; must-spend is untouched.
      </p>
      {(plan.budget.ageBands ?? []).map((band, i) => (
        <div key={i} className="inputs-grid item-card" style={{ padding: 'var(--space-3) var(--space-4)' }}>
          <NumberField label="From age" value={band.startAge} min={55} max={100}
            onCommit={(v) => update((p) => { p.budget.ageBands![i]!.startAge = v; })} />
          <NumberField label="Like-to-spend %" min={0} max={150}
            value={Math.round(band.discretionaryMultiplier * 100)}
            onCommit={(v) => update((p) => { p.budget.ageBands![i]!.discretionaryMultiplier = v / 100; })} />
          <div className="field" style={{ justifyContent: 'flex-end' }}>
            <button className="btn danger"
              onClick={() => update((p) => { p.budget.ageBands!.splice(i, 1); })}>
              Remove band
            </button>
          </div>
        </div>
      ))}
      <div className="add-row">
        {!plan.budget.ageBands?.length && (
          <button className="btn"
            onClick={() => update((p) => {
              p.budget.ageBands = [
                { startAge: 75, discretionaryMultiplier: 0.85 },
                { startAge: 85, discretionaryMultiplier: 0.7 },
              ];
            })}>
            Apply go-go / slow-go / no-go template
          </button>
        )}
        <button className="btn"
          onClick={() => update((p) => {
            p.budget.ageBands ??= [];
            p.budget.ageBands.push({ startAge: 80, discretionaryMultiplier: 0.8 });
          })}>
          + Add band
        </button>
      </div>
    </div>
  );
}
